import React, { useState, useEffect } from 'react';
import { useAuth } from '../auth/AuthContext';
import { loadDefinitions, CATEGORIES } from '../store/assetDefinitions';

// SVG Icons matching CENEC knowledge base style
export const Icons = {
  nodes: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="4"/><circle cx="4" cy="4" r="2"/><circle cx="20" cy="4" r="2"/>
      <circle cx="4" cy="20" r="2"/><circle cx="20" cy="20" r="2"/>
      <line x1="6" y1="6" x2="9" y2="9"/><line x1="15" y1="9" x2="18" y2="6"/>
      <line x1="9" y1="15" x2="6" y2="18"/><line x1="15" y1="15" x2="18" y2="18"/>
    </svg>
  ),
  lines: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="4" cy="12" r="3"/><circle cx="20" cy="12" r="3"/>
      <line x1="7" y1="12" x2="17" y2="12"/>
      <circle cx="12" cy="4" r="2"/><circle cx="12" cy="20" r="2"/>
      <line x1="12" y1="6" x2="12" y2="10"/><line x1="12" y1="14" x2="12" y2="18"/>
    </svg>
  ),
  definitions: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z"/>
      <polyline points="14 2 14 8 20 8"/><line x1="16" y1="13" x2="8" y2="13"/>
      <line x1="16" y1="17" x2="8" y2="17"/><polyline points="10 9 9 9 8 9"/>
    </svg>
  ),
  climate: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/>
      <line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/>
      <line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/>
      <line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/>
      <line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/>
    </svg>
  ),
  canvas: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="3" width="7" height="7"/><rect x="14" y="3" width="7" height="7"/>
      <rect x="14" y="14" width="7" height="7"/><rect x="3" y="14" width="7" height="7"/>
    </svg>
  ),
  users: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/>
      <circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/>
      <path d="M16 3.13a4 4 0 0 1 0 7.75"/>
    </svg>
  ),
  logout: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"/>
      <polyline points="16 17 21 12 16 7"/><line x1="21" y1="12" x2="9" y2="12"/>
    </svg>
  ),
  gridMarket: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <polyline points="13 2 4 14 12 14 11 22 20 10 12 10 13 2"/>
    </svg>
  ),
  frequency: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M2 12 L5 12 L7 4 L11 20 L13 8 L15 16 L17 12 L22 12"/>
    </svg>
  ),
  commodities: (
    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
      <path d="M3 3v18h18"/>
      <polyline points="7 15 11 11 14 14 21 7"/>
      <polyline points="15 7 21 7 21 13"/>
    </svg>
  ),
};

const DB_API = process.env.REACT_APP_API_URL || `http://${window.location.hostname}:5001`;

const CenecSidebar = ({ activeView, onViewChange, onShowUsers }) => {
  const { user, logout } = useAuth();
  const [expandedCategory, setExpandedCategory] = useState(null);
  const [definitions, setDefinitions] = useState([]);
  const [dbSize, setDbSize] = useState(null);

  useEffect(() => {
    let cancelled = false;
    loadDefinitions().then((defs) => {
      if (!cancelled) setDefinitions(defs);
    });
    return () => { cancelled = true; };
  }, [activeView]);

  // Fetch DB size on mount and every 60 seconds
  useEffect(() => {
    const fetchSize = () => {
      fetch(`${DB_API}/api/db-size`)
        .then(r => r.ok ? r.json() : null)
        .then(d => { if (d) setDbSize(d); })
        .catch(() => {});
    };
    fetchSize();
    const interval = setInterval(fetchSize, 60000);
    return () => clearInterval(interval);
  }, []);

  const onDragStart = (event, def) => {
    event.dataTransfer.setData('application/reactflow', JSON.stringify(def));
    event.dataTransfer.effectAllowed = 'move';
  };

  return (
    <div className="cenec-sidebar">
      {/* Brand */}
      <div className="sidebar-brand">
        <img src="/cenec-logo.svg" alt="CENEC" className="sidebar-logo" />
        <small>Network Resource Grid</small>
      </div>

      {/* Navigation — Main */}
      <nav className="sidebar-nav">
        <a href="#projects" className={activeView === 'projects' ? 'active' : ''} onClick={(e) => { e.preventDefault(); onViewChange('projects'); }}>
          <span className="nav-icon">{Icons.canvas}</span>
          Projects
        </a>
        <a href="#climate" className={activeView === 'climate' ? 'active' : ''} onClick={(e) => { e.preventDefault(); onViewChange('climate'); }}>
          <span className="nav-icon">{Icons.climate}</span>
          Climate Data
        </a>
        <a href="#frequency" className={activeView === 'frequency' ? 'active' : ''} onClick={(e) => { e.preventDefault(); onViewChange('frequency'); }}>
          <span className="nav-icon">{Icons.frequency}</span>
          Frequency
        </a>
        <a href="#commodities" className={activeView === 'commodities' ? 'active' : ''} onClick={(e) => { e.preventDefault(); onViewChange('commodities'); }}>
          <span className="nav-icon">{Icons.commodities}</span>
          Commodities
        </a>
      </nav>

      {/* Administration section */}
      <div className="sidebar-section-title">Administration</div>
      <nav className="sidebar-nav sidebar-nav-admin">
        <a href="#definitions" className={activeView === 'definitions' ? 'active' : ''} onClick={(e) => { e.preventDefault(); onViewChange('definitions'); }}>
          <span className="nav-icon">{Icons.definitions}</span>
          Asset Definitions
        </a>
        <a href="#nodes" className={activeView === 'nodes' ? 'active' : ''} onClick={(e) => { e.preventDefault(); onViewChange('nodes'); }}>
          <span className="nav-icon">{Icons.nodes}</span>
          Node Definitions
        </a>
        <a href="#lines" className={activeView === 'lines' ? 'active' : ''} onClick={(e) => { e.preventDefault(); onViewChange('lines'); }}>
          <span className="nav-icon">{Icons.lines}</span>
          Line Definitions
        </a>
        <a href="#media" className={activeView === 'media' ? 'active' : ''} onClick={(e) => { e.preventDefault(); onViewChange('media'); }}>
          <span className="nav-icon">{Icons.climate}</span>
          Process Media
        </a>
        <a href="#pvsystems" className={activeView === 'pvsystems' ? 'active' : ''} onClick={(e) => { e.preventDefault(); onViewChange('pvsystems'); }}>
          <span className="nav-icon">{Icons.climate}</span>
          PV Systems
        </a>
        <a href="#system" className={activeView === 'system' ? 'active' : ''} onClick={(e) => { e.preventDefault(); onViewChange('system'); }}>
          <span className="nav-icon">{Icons.canvas}</span>
          System Modules
        </a>
        <a href="#gridmarket" className={activeView === 'gridmarket' ? 'active' : ''} onClick={(e) => { e.preventDefault(); onViewChange('gridmarket'); }}>
          <span className="nav-icon">{Icons.gridMarket}</span>
          Grid/Market
        </a>
        {user?.role === 'admin' && (
          <a href="#users" className={activeView === 'users' ? 'active' : ''} onClick={(e) => { e.preventDefault(); onShowUsers(); onViewChange('users'); }}>
            <span className="nav-icon">{Icons.users}</span>
            User Management
          </a>
        )}
      </nav>

      {/* Asset Library for canvas drag — only show on canvas view */}
      {activeView === 'canvas' && (
        <>
          <div className="sidebar-section-title">Asset Library</div>
          <div className="sidebar-assets">
            {CATEGORIES.map((category) => {
              const items = definitions.filter((d) => d.category === category.id);
              if (items.length === 0) return null;
              const isExpanded = expandedCategory === category.id;

              return (
                <div key={category.id} className="asset-category">
                  <button
                    className="category-toggle"
                    onClick={() => setExpandedCategory(isExpanded ? null : category.id)}
                  >
                    <span className="category-dot" style={{ background: category.color }} />
                    <span>{category.label}</span>
                    <svg
                      className={`chevron ${isExpanded ? 'open' : ''}`}
                      width="14" height="14" viewBox="0 0 24 24"
                      fill="none" stroke="currentColor" strokeWidth="2"
                    >
                      <polyline points="6 9 12 15 18 9" />
                    </svg>
                  </button>
                  {isExpanded && (
                    <div className="category-items">
                      {items.map((def) => (
                        <div
                          key={def.id}
                          className="asset-drag-item"
                          draggable
                          onDragStart={(e) => onDragStart(e, def)}
                        >
                          <span className="asset-drag-icon">{def.icon}</span>
                          <span>{def.name}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </>
      )}

      {/* Help */}
      <div className="sidebar-help-section">
        <div className="sidebar-help-text">
          {activeView === 'canvas'
            ? 'Drag assets to canvas. Connect handles to link.'
            : activeView === 'climate'
            ? 'Manage locations & download satellite/reanalysis climate data.'
            : 'Define database fields, shapes, and connectors for each asset type.'}
        </div>
      </div>

      {/* DB Size */}
      <div className="sidebar-db-size">
        <span className={`db-dot ${dbSize ? '' : 'offline'}`} />
        {dbSize
          ? `DB: ${dbSize.total_size}`
          : 'DB: offline'}
      </div>

      {/* User */}
      <div className="sidebar-bottom">
        <div className="sidebar-user">
          <span className="sidebar-user-avatar">{user?.avatar}</span>
          <div className="sidebar-user-info">
            <span className="sidebar-user-name">{user?.name}</span>
            <span className="sidebar-user-role">{user?.role} &middot; {user?.org}</span>
          </div>
          <button className="btn-logout" onClick={logout} title="Sign out">
            {Icons.logout}
          </button>
        </div>
      </div>
    </div>
  );
};

export default CenecSidebar;
